import {useInfiniteQuery} from '@tanstack/react-query';
import {fetchRecords} from '../services/ReconciliationReportsService';
import {useState, useSyncExternalStore} from 'react';
import {BillingChannel} from '../types.ts';

// FNP-535: read-only module. No create/update/delete mutations — the ChannelReconciler
// (Job B) owns the lifecycle. This hook is list-fetch only.
//
// listReconciliationReports REQUIRES a channel (the channel-runStartedAt-index partition
// key), so the list is always scoped to exactly one channel. FNP-607: the channel is the
// module's DEFAULT FILTER (defaults to STRIPE so the list works out of the box) and is
// surfaced through the standard Filter button/drawer/form like every other module — but it
// is REQUIRED and NON-CLEARABLE: there is always exactly one channel selected, so the store
// is a non-null BillingChannel and "Clear" resets to STRIPE rather than to none. (Clearing
// to none was considered and rejected as needless complication — a channel-less list has no
// meaning here.)
export const DEFAULT_CHANNEL: BillingChannel = 'STRIPE';

let globalChannel: BillingChannel = DEFAULT_CHANNEL;
const channelListeners: Set<() => void> = new Set();

const notifyChannelChange = (channel: BillingChannel) => {
    globalChannel = channel;
    channelListeners.forEach(listener => listener());
};

// External store wired through useSyncExternalStore (FNP-593): the previous
// useState(globalChannel) + useEffect-registered listener could miss a change
// fired between a consumer's first render and its effect commit, briefly
// desyncing the selector and the list. useSyncExternalStore subscribes during
// render and reads the live snapshot, so there is no race and no tearing.
const subscribeChannel = (listener: () => void) => {
    channelListeners.add(listener);
    return () => {
        channelListeners.delete(listener);
    };
};

const getChannelSnapshot = (): BillingChannel => globalChannel;

// Test-only escape hatch — resets the module-scoped store to its default. Tests
// share the store, so without this they leak state between cases.
export const __resetReconciliationReportsChannelForTesting = () => {
    notifyChannelChange(DEFAULT_CHANNEL);
};

export const useReconciliationReportsChannel = () => {
    const channel = useSyncExternalStore(subscribeChannel, getChannelSnapshot);

    const handleSetChannel = (newChannel: BillingChannel) => {
        notifyChannelChange(newChannel);
    };

    return {channel, handleSetChannel};
};

export const useReconciliationReports = () => {
    const {channel} = useReconciliationReportsChannel();
    const [limit] = useState(10);

    // INFINITE QUERY — channel is part of the cache key, so switching the channel
    // refetches automatically. The channel-runStartedAt-index is ordered DESC
    // server-side (newest run first).
    const reportsQuery = useInfiniteQuery({
        queryKey: ['reconciliationReports', channel, limit],
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({channel, limit, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const fetchRecordsAction = () => {
        if (reportsQuery.hasNextPage) {
            reportsQuery.fetchNextPage();
        }
    };

    const reload = () => {
        reportsQuery.refetch();
    };

    // The service layer guarantees page.items is a real array even on a GraphQL-level
    // error; filter(Boolean) keeps an undefined row out of the table as defense-in-depth.
    const allItems = (reportsQuery.data?.pages?.flatMap(page => page?.items ?? []) || []).filter(Boolean);

    return {
        items: allItems,
        visibleItems: allItems,
        loading: reportsQuery.isLoading,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: reportsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        reload,
    };
};
